import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';

// Wraps autosuggest component with current form library being used (Formik)
// Currently used for Autosuggest with is PROMISE based rather than Observer based
const withAutosuggestWrapper = (AutoSuggestComponent, wrapperTools = {
   handleCustomError: (errorPtr = {}) => {
      return errorPtr?.id === 'Required!';
   },
}) => {
   const WrappedComponent = (props) => {
      const [isTouched, setIsTouched] = useState(false);
      const [errorMessage, setErrorMessage] = useState();
      const { values } = useFormikContext();
      const [localQueryParams, setLocalQueryParams] = useState({});

      useEffect(() => {
         // Used for getting the errorMessage when NumberInput component is used as a FieldArray Field
         const nameParts = props.field.name.split(/\.|\[|]/).filter((s) => s);
         let errorPtr = props.form.errors;

         for (let i = 0; i < nameParts.length; i++) {
            const name = nameParts[i];
            if (errorPtr === undefined) {
               break;
            } else if (lodash.isObject(errorPtr)) {
               errorPtr = errorPtr[name];
            } else {
               errorPtr = undefined;
               break;
            }
         }

         if (typeof errorPtr === 'string' || typeof errorPtr === 'undefined') {
            setErrorMessage(errorPtr);
         } else if (lodash.isObject(errorPtr) && wrapperTools.handleCustomError(errorPtr)) {
            setErrorMessage('Please select a value from the autosuggest.');
         }
      }, [JSON.stringify(props.form.errors)]);

      useEffect(() => {
         const nameParts = props.field.name.split(/\.|\[|]/).filter((s) => s);
         let touchedPtr = props.form.touched;
         for (let i = 0; i < nameParts.length; i++) {
            const name = nameParts[i];
            if (touchedPtr === undefined) {
               break;
            } else if (lodash.isObject(touchedPtr) && Object.prototype.hasOwnProperty.call(touchedPtr, name)) {
               touchedPtr = touchedPtr[name];
            } else if (lodash.isArray(touchedPtr) && +name < touchedPtr.length) {
               touchedPtr = touchedPtr[name];
            } else {
               touchedPtr = undefined;
               break;
            }
         }

         let newIsTouched = false;
         if (touchedPtr !== undefined && lodash.isBoolean(touchedPtr)) {
            newIsTouched = touchedPtr;
         }
         setIsTouched(newIsTouched);
      }, [JSON.stringify(props.form.touched)]);

      // when values changes autoSuggest dependencyProp values
      useEffect(() => {
         const dependsOn = props.autosuggestProps?.dependsOn;
         if (lodash.isObject(dependsOn) && !lodash.isEmpty(dependsOn)) {
            const newLocalQueryParams = {};
            const valueEntries = Object.entries(values);
            for (let i = 0; i < valueEntries.length; i++) {
               const [key, value] = valueEntries[i];
               if (Object.prototype.hasOwnProperty.call(dependsOn, key)) {
                  const [keyDependency, valueDependency] = dependsOn[key](value);
                  newLocalQueryParams[keyDependency] = valueDependency;
               }
            }
            setLocalQueryParams(newLocalQueryParams);
         }
      }, [values]);

      const handleChange = useCallback((newValue) => {
         props.form.setFieldValue(props.field.name, newValue);

         if (errorMessage) {
            props.form.setFieldError(props.field.name, undefined);
         }
      }, [props.field.name, errorMessage]);

      const handleBlur = useCallback(() => {
         props.form.setFieldTouched(props.field.name, true);
      }, []);

      return (
         <AutoSuggestComponent
            {...props.field}
            handleChange={handleChange}
            handleBlur={handleBlur}
            isDisabled={props.isDisabled}
            isTouched={isTouched}
            errorMessage={errorMessage}
            queryParams={{
               ...props.queryParams,
               ...localQueryParams,
            }}
            filterSuggestions={props.filterSuggestions}
         />
      );
   };

   WrappedComponent.propTypes = {
      field: PropTypes.shape({
         name: PropTypes.string.isRequired,
         onChange: PropTypes.func.isRequired,
         onBlur: PropTypes.func.isRequired,
         value: PropTypes.oneOfType([
            PropTypes.shape({}),
            PropTypes.string,
         ]),
      }).isRequired,
      form: PropTypes.shape({
         setFieldTouched: PropTypes.func.isRequired,
         setFieldValue: PropTypes.func.isRequired,
         touched: PropTypes.shape({}).isRequired,
         errors: PropTypes.shape({}).isRequired,
      }).isRequired,
      isDisabled: PropTypes.bool,
      queryParams: PropTypes.shape({}),
      filterSuggestions: PropTypes.func,
      autosuggestProps: PropTypes.shape({
         dependsOn: PropTypes.objectOf(PropTypes.func).isRequired,
      }),
   };

   WrappedComponent.defaultProps = {
      isDisabled: false,
      queryParams: {},
      filterSuggestions: null,
      autosuggestProps: undefined,
   };

   return WrappedComponent;
};

export default withAutosuggestWrapper;
